import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChatProvider } from "@/contexts/ChatContext";
import { useChatContext } from "@/hooks/useChatContext";
import { ChatHeader } from "@/components/agent/chat/ChatHeader";
import { ChatContainer } from "@/components/agent/chat/ChatContainer";
import { ThankYouScreen } from "@/components/agent/chat/ThankYouScreen";
import { useIsMobile } from "@/hooks/use-mobile";
import { DesktopChatLayout } from "@/components/agent/chat/DesktopChatLayout";
import { PublicAgentNavBar } from "@/components/agent/chat/PublicAgentNavBar";
import EdgeFunctionDebugView from "@/components/agent/debug/EdgeFunctionDebugView";
import { Database } from "@/types/database.types";

type Tables<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Row"];
import { UnifiedChatEngine } from "@/components/chat/ChatEngine";
import { UnifiedChatHeader } from "@/components/chat/UnifiedChatHeader";

type Agent = Tables<"agents">;

type AgentPreviewTabProps = {
  agent: Agent;
  organization?: any;
  isPublic?: boolean;
  onError?: (message: string) => void;
  brandColor?: string;
  textColor?: string;
  hideHeader?: boolean;
  isMobile?: boolean;
  userInfo?: { name?: string; email?: string } | null;
};

// Check if we should use the new unified system (feature flag for safe testing)
const USE_UNIFIED_CHAT = false; // Set to true to test new system

// Inner component using the context
const AgentPreviewContent = ({ hideHeader = false, isMobile = false }) => {
  const {
    agent,
    organization,
    isPublic,
    conversationState,
    conversationId,
    handleResetConversation,
    handleReopenConversation,
    brandColor,
    textColor,
    agentChatUser,
    debugMode,
  } = useChatContext();

  // Default thank you message
  const defaultThankYouMessage =
    "This conversation has been completed. Start a new conversation if you need further assistance.";

  // Get thank you message from agent or use default
  const thankYouMessage = agent?.thank_you_message || defaultThankYouMessage;

  // Check if the conversation is completed
  const isConversationCompleted = conversationState === "completed";

  // Determine if we should use the desktop layout based on isMobile flag
  const shouldUseDesktopLayout = !isMobile && isPublic;

  // NEW UNIFIED SYSTEM (when feature flag is enabled)
  if (USE_UNIFIED_CHAT) {
    return (
      <div
        className="flex flex-col h-full w-full overflow-hidden bg-white"
        style={
          {
            "--primary": brandColor,
            "--primary-foreground":
              brandColor === "#ffffff" || brandColor === "#fff"
                ? "#000000"
                : "#ffffff",
          } as React.CSSProperties
        }
      >
        {/* Header - unified approach */}
        {!hideHeader && (
          <div className="flex-shrink-0">
            <UnifiedChatHeader
              type="basic"
              agent={agent}
              organization={organization}
              brandColor={brandColor}
            />
          </div>
        )}

        {/* Main content */}
        {isConversationCompleted ? (
          <ThankYouScreen
            message={thankYouMessage}
            brandColor={brandColor}
            textColor={textColor}
            onStartNew={handleResetConversation}
            onResumeConversation={handleReopenConversation}
          />
        ) : (
          <div className="flex-1 overflow-hidden">
            <UnifiedChatEngine
              mode={isPublic ? "external" : "internal"}
              layout={shouldUseDesktopLayout ? "fullpage" : "widget"}
              headerType="none" // Header handled separately above
              showSplash={isPublic} // Only show splash for public (external) views
              isEmbedded={false}
              constrainWidth={shouldUseDesktopLayout}
              isMobile={isMobile}
              agent={agent}
              organization={organization}
              userInfo={agentChatUser}
              brandColor={brandColor}
              textColor={textColor}
            />
          </div>
        )}

        {debugMode && (
          <EdgeFunctionDebugView agentId={agent.id} show={debugMode} />
        )}
      </div>
    );
  }

  // LEGACY SYSTEM (current implementation - preserved exactly)
  if (shouldUseDesktopLayout) {
    return (
      <>
        <DesktopChatLayout
          agent={agent}
          organization={organization}
          brandColor={brandColor}
          userInfo={agentChatUser}
          isConversationCompleted={isConversationCompleted}
          thankYouMessage={thankYouMessage}
          onStartNew={handleResetConversation}
          onResumeConversation={handleReopenConversation}
          hideHeader={hideHeader}
        />
        {debugMode && (
          <EdgeFunctionDebugView agentId={agent.id} show={debugMode} />
        )}
      </>
    );
  }

  return (
    <div
      className="relative h-full w-full max-w-full overflow-hidden bg-white"
      style={
        {
          "--primary": brandColor,
          "--primary-foreground":
            brandColor === "#ffffff" || brandColor === "#fff"
              ? "#000000"
              : "#ffffff",
        } as React.CSSProperties
      }
    >
      {/* Show navbar for public agents or header for internal - now positioned independently */}
      {!hideHeader && (
        <>
          {isPublic ? (
            <PublicAgentNavBar
              organization={organization}
              agent={agent}
              brandColor={brandColor}
            />
          ) : (
            <div className="flex-shrink-0">
              <ChatHeader
                agent={agent}
                brandColor={brandColor}
                isPublic={isPublic}
                organization={organization}
              />
            </div>
          )}
        </>
      )}

      {/* Main content area - full height */}
      <div className="h-full w-full max-w-full overflow-hidden">
        {isConversationCompleted ? (
          <ThankYouScreen
            message={thankYouMessage}
            brandColor={brandColor}
            textColor={textColor}
            onStartNew={handleResetConversation}
            onResumeConversation={handleReopenConversation}
          />
        ) : (
          <ChatContainer isMobile={isMobile} />
        )}
      </div>

      {debugMode && (
        <EdgeFunctionDebugView agentId={agent.id} show={debugMode} />
      )}
    </div>
  );
};

const AgentPreviewTab = ({
  agent,
  organization,
  isPublic = false,
  onError,
  brandColor,
  textColor = "#FFFFFF",
  hideHeader = false,
  isMobile = false,
  userInfo = null,
}: AgentPreviewTabProps) => {
  const actualIsMobile = isMobile || useIsMobile();

  // Component mounted - debug info available in dev tools if needed

  return (
    <ChatProvider
      agent={agent}
      organization={organization}
      isPublic={isPublic}
      onError={onError}
      brandColor={brandColor}
      textColor={textColor}
      userInfo={userInfo}
    >
      <div className="h-full w-full max-w-full overflow-hidden">
        <AgentPreviewContent
          hideHeader={hideHeader}
          isMobile={actualIsMobile}
        />
      </div>
    </ChatProvider>
  );
};

export default AgentPreviewTab;
